// apps/frontend/admin-ui/src/pages/currencies.ts
import {
  AdminApiError,
  type AdminClient,
  type SupportedCurrencyView,
} from '@apx/admin-sdk';
import { emptyState } from '../empty-state.js';
import { customModal, html } from '../modal.js';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { currencyDecimals } from '@apx/money';

/** "2 · counted in cents" / "0 · whole units" — the platform's fixed decimals
 *  for a code, spelled out. */
function decimalsLabel(code: string): string {
  const d = currencyDecimals(code);
  return d === 0 ? '0 · whole units' : `${d} · counted in ${d === 2 ? 'cents' : `1/${10 ** d} units`}`;
}

const DECIMALS_NOTE =
  'Decimals are fixed by the platform and cannot be set here: USD has 2 (amounts are counted in cents), every other currency has 0 (whole units). Every stored amount depends on it.';

/**
 * Platform-level currency registry (super_admin only). Native-currency model:
 * a currency IS the unit of account for a game+integrator, not a fiat
 * conversion target — this page manages which codes exist, their display
 * metadata, and whether they're enabled. Per-(game,currency) bet limits are a
 * separate page (table-limits.ts).
 *
 * A currency's DECIMALS are not editable here (#519). They are fixed by the
 * platform — USD 2, counted in cents; every other currency 0, counted in whole
 * units — because every stored amount is an integer count of that minor unit:
 * changing the number would re-scale every balance, bet and limit already
 * stored in the currency. The column shows the platform's value, read from
 * `@apx/money`; the API rejects a request that names a different one.
 */
export async function renderCurrenciesPage(
  root: HTMLElement,
  deps: { client: AdminClient },
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Currencies</h1>
    <p class="page-sub">The platform-wide supported-currency registry. There is no fiat conversion here — the currency itself is the unit of account. Disabling a currency does not delete it; there is no delete. <strong>Decimals are fixed by the platform</strong>: USD has 2, so its amounts are counted in cents; every other currency has 0 and is counted in whole units. They are read-only because every stored balance, bet and limit depends on them.</p>

    <div class="card">
      <div class="detail-card-head" style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px">
        <div>
          <h2>Registry</h2>
          <span class="muted">Ordered by sort order, then code.</span>
        </div>
        <button id="cur-create" class="primary" style="white-space:nowrap">+ Add currency</button>
      </div>
      <div id="cur-list">${skeletonTable({ rows: 5, columns: 7 })}</div>
    </div>
  `;

  const listEl = root.querySelector<HTMLElement>('#cur-list')!;

  async function refresh(): Promise<void> {
    try {
      const currencies = await deps.client.listCurrencies();
      listEl.innerHTML = renderTable(currencies);
      bindRowActions(currencies);
    } catch (err) {
      listEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(currencies: SupportedCurrencyView[]): string {
    if (currencies.length === 0) {
      return emptyState({
        icon: 'list',
        title: 'No currencies registered',
        hint: 'Add one with “+ Add currency”.',
      });
    }
    return `<table>
      <thead>
        <tr>
          <th>Code</th>
          <th>Display name</th>
          <th>Symbol</th>
          <th data-tip="Fixed by the platform — not editable">Decimals</th>
          <th>Sort</th>
          <th>Enabled</th>
          <th style="text-align:right">Actions</th>
        </tr>
      </thead>
      <tbody>${currencies.map(rowHtml).join('')}</tbody>
    </table>`;
  }

  function rowHtml(c: SupportedCurrencyView): string {
    return `<tr>
      <td><code>${escapeHtml(c.code)}</code></td>
      <td>${escapeHtml(c.displayName)}</td>
      <td>${c.symbol ? escapeHtml(c.symbol) : '<span class="muted">—</span>'}</td>
      <td><span class="muted">${escapeHtml(decimalsLabel(c.code))}</span></td>
      <td>${c.sortOrder}</td>
      <td>
        <button data-toggle="${escapeHtml(c.code)}:${c.enabled}" class="pill ${c.enabled ? 'live' : 'warn'}" data-tip="Click to toggle">
          ${c.enabled ? 'enabled' : 'disabled'}
        </button>
      </td>
      <td style="text-align:right">
        <button data-edit="${escapeHtml(c.code)}" class="ghost">Edit</button>
      </td>
    </tr>`;
  }

  function bindRowActions(currencies: SupportedCurrencyView[]): void {
    listEl.querySelectorAll<HTMLButtonElement>('button[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const [code, current] = btn.dataset.toggle!.split(':');
        const next = current !== 'true';
        try {
          await deps.client.updateCurrency(code!, { enabled: next });
          showToast(`${code} ${next ? 'enabled' : 'disabled'}.`, 'success');
          await refresh();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });

    listEl.querySelectorAll<HTMLButtonElement>('button[data-edit]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const code = btn.dataset.edit!;
        const current = currencies.find((c) => c.code === code);
        if (current) void openEditModal(current);
      });
    });
  }

  async function openCreateModal(): Promise<void> {
    const result = await customModal<{
      code: string;
      displayName: string;
      symbol: string;
      sortOrder: number;
      enabled: boolean;
    }>({
      title: 'Add currency',
      confirmLabel: 'Create',
      body: html`
        <div class="modal-field">
          <label class="modal-field-label">Code (3–10 uppercase letters)</label>
          <input id="cur-c-code" type="text" placeholder="USD" maxlength="10" class="modal-field-input" style="text-transform:uppercase" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Display name</label>
          <input id="cur-c-name" type="text" placeholder="US Dollar" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Symbol (optional, max 8 chars)</label>
          <input id="cur-c-symbol" type="text" placeholder="$" maxlength="8" class="modal-field-input" />
        </div>
        <p class="muted" style="margin:0 0 12px;font-size:12px">${DECIMALS_NOTE}</p>
        <div class="modal-field">
          <label class="modal-field-label">Sort order</label>
          <input id="cur-c-sort" type="number" value="0" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label style="display:flex;align-items:center;gap:8px;font-size:13px">
            <input id="cur-c-enabled" type="checkbox" checked />
            Enabled
          </label>
        </div>
        <p id="cur-c-err" class="error" style="margin:8px 0 0;font-size:12px;min-height:14px"></p>
      `,
      parseOnConfirm: (r) => {
        const code = r.querySelector<HTMLInputElement>('#cur-c-code')!.value.trim().toUpperCase();
        const displayName = r.querySelector<HTMLInputElement>('#cur-c-name')!.value.trim();
        const symbol = r.querySelector<HTMLInputElement>('#cur-c-symbol')!.value.trim();
        const sortOrder = Number(r.querySelector<HTMLInputElement>('#cur-c-sort')!.value);
        const enabled = r.querySelector<HTMLInputElement>('#cur-c-enabled')!.checked;
        const err = r.querySelector<HTMLElement>('#cur-c-err')!;
        if (!/^[A-Z]{3,10}$/.test(code)) {
          err.textContent = 'Code must be 3–10 uppercase letters.';
          return null;
        }
        if (!displayName || displayName.length > 120) {
          err.textContent = 'Display name is required (max 120 chars).';
          return null;
        }
        if (symbol.length > 8) {
          err.textContent = 'Symbol max 8 chars.';
          return null;
        }
        if (!Number.isInteger(sortOrder)) {
          err.textContent = 'Sort order must be an integer.';
          return null;
        }
        return { code, displayName, symbol, sortOrder, enabled };
      },
    });
    if (!result) return;
    try {
      const created = await deps.client.createCurrency({
        code: result.code,
        displayName: result.displayName,
        symbol: result.symbol || undefined,
        // No `decimals`: the platform fixes it per code.
        sortOrder: result.sortOrder,
        enabled: result.enabled,
      });
      showToast(`Created ${created.code}.`, 'success');
      await refresh();
    } catch (err) {
      if (err instanceof AdminApiError && err.code === 'CURRENCY_ALREADY_EXISTS') {
        showToast(`${result.code} already exists.`, 'error');
      } else {
        showToast(describe(err), 'error');
      }
    }
  }

  async function openEditModal(current: SupportedCurrencyView): Promise<void> {
    const result = await customModal<{
      displayName: string;
      symbol: string;
      sortOrder: number;
      enabled: boolean;
    }>({
      title: `Edit ${current.code}`,
      confirmLabel: 'Save',
      body: html`
        <div class="modal-field">
          <label class="modal-field-label">Display name</label>
          <input id="cur-e-name" type="text" value="${current.displayName}" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Symbol (optional, max 8 chars)</label>
          <input id="cur-e-symbol" type="text" value="${current.symbol ?? ''}" maxlength="8" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Decimals (fixed by the platform)</label>
          <div class="muted" style="font-size:13px">${decimalsLabel(current.code)}</div>
          <p class="muted" style="margin:6px 0 0;font-size:12px">${DECIMALS_NOTE}</p>
        </div>
        <div class="modal-field">
          <label class="modal-field-label">Sort order</label>
          <input id="cur-e-sort" type="number" value="${current.sortOrder}" class="modal-field-input" />
        </div>
        <div class="modal-field">
          <label style="display:flex;align-items:center;gap:8px;font-size:13px">
            <input id="cur-e-enabled" type="checkbox" ${current.enabled ? 'checked' : ''} />
            Enabled
          </label>
        </div>
        <p id="cur-e-err" class="error" style="margin:8px 0 0;font-size:12px;min-height:14px"></p>
      `,
      parseOnConfirm: (r) => {
        const displayName = r.querySelector<HTMLInputElement>('#cur-e-name')!.value.trim();
        const symbol = r.querySelector<HTMLInputElement>('#cur-e-symbol')!.value.trim();
        const sortOrder = Number(r.querySelector<HTMLInputElement>('#cur-e-sort')!.value);
        const enabled = r.querySelector<HTMLInputElement>('#cur-e-enabled')!.checked;
        const err = r.querySelector<HTMLElement>('#cur-e-err')!;
        if (!displayName || displayName.length > 120) {
          err.textContent = 'Display name is required (max 120 chars).';
          return null;
        }
        if (symbol.length > 8) {
          err.textContent = 'Symbol max 8 chars.';
          return null;
        }
        if (!Number.isInteger(sortOrder)) {
          err.textContent = 'Sort order must be an integer.';
          return null;
        }
        return { displayName, symbol, sortOrder, enabled };
      },
    });
    if (!result) return;
    try {
      await deps.client.updateCurrency(current.code, {
        displayName: result.displayName,
        symbol: result.symbol.length > 0 ? result.symbol : null,
        // No `decimals`: the platform fixes it per code.
        sortOrder: result.sortOrder,
        enabled: result.enabled,
      });
      showToast(`${current.code} updated.`, 'success');
      await refresh();
    } catch (err) {
      showToast(describe(err), 'error');
    }
  }

  root.querySelector<HTMLButtonElement>('#cur-create')!.addEventListener('click', () => void openCreateModal());

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}
